import { QueryClient } from '@tanstack/react-query';
import { ApiError } from './api/client';

// Query client defaults, tuned to this application's actual usage rather than
// left at library defaults.
//
// The shape of the app: staff sit on one screen for minutes at a time while
// colleagues mutate the same rows from other screens (a nurse completes vitals
// while the doctor's queue is open). So refetch-on-focus is genuinely useful
// here, unlike in a single-user tool where it is mostly noise.
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Clinical and queue data goes stale fast — a 30s window keeps a screen
      // responsive without hammering the API on every remount.
      staleTime: 30_000,
      gcTime: 5 * 60_000,

      refetchOnWindowFocus: true,
      refetchOnReconnect: true,

      // Retrying a 4xx is pointless and actively harmful for some codes: a 412
      // conflict-stale means someone else wrote first and the client must
      // refetch and re-apply, not blindly repeat the same write; a 403 means
      // the branch or module grant is missing and will still be missing.
      // Only genuine server/network faults are worth a second attempt.
      retry: (failureCount, error) => {
        if (error instanceof ApiError) return error.isTransient && failureCount < 2;
        return failureCount < 2;
      },
      retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
    },

    mutations: {
      // Never auto-retry a mutation. Several of these endpoints are not
      // idempotent — POST /api/prescriptions creates a new prescription, and
      // billing mints a sequential GST invoice number that legally must not
      // have gaps or duplicates (see backend/utils/billNumbering.js). A
      // transparent retry on a timed-out-but-succeeded request would create
      // two.
      retry: false,
    },
  },
});
